import { Card, CardFooter, CardHeader } from "@fanduel/og-fe-library";
import { useMemo } from "react";
import ArrowRight from "@/components/svg/ArrowRight";
import {
  SPORTSBOOK_URL,
  SPORTSBOOK_ODDS_CTA_CLICKED,
} from "@/constants/amplitude";
import { getFormattedDateWithTimeAndTimezone } from "@/utils/date";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useArticleInfo } from "@/utils/hooks/useArticleContext";
import { filterEmpty } from "@/utils/index";
import { DynamicOdds, Team } from "../tempTypes";
import TeamsLine from "./TeamsLine";
import styles from "./teamProps.module.scss";

interface TeamPropsProps extends DynamicOdds {
  isFeaturedBet?: boolean;
  authorName?: string;
  sportName?: string;
}

const TeamProps = ({
  odds,
  event,
  isFeaturedBet = false,
  authorName,
  sportName,
}: TeamPropsProps) => {
  const teams = useMemo(
    () => ({
      homeTeam: { ...odds.event?.homeTeam, propBets: odds.props } as Team,
      awayTeam: { ...odds.event?.awayTeam, propBets: odds.props } as Team,
    }),
    [odds?.event?.awayTeam, odds?.event?.homeTeam, odds.props]
  );

  const { sessionId, deviceId, logAmplitudeEvent } = useAmplitude();
  const { title, sport, author } = useArticleInfo();
  const ampBaseUrl = odds?.event?.eventUrl || SPORTSBOOK_URL;
  const ampLink = ampBaseUrl;
  const parsedAuthorName = author ?? authorName;
  const parsedSportName = sportName ?? sport;

  const sbLinkClicked = () => {
    logAmplitudeEvent(SPORTSBOOK_ODDS_CTA_CLICKED, {
      "Link Text": "more_odds_in_sportsbook",
      "Link URL": ampBaseUrl,
      Module: isFeaturedBet ? "featured_bet" : "coupon",
      "Event Name": event.name ?? "",
      Competition: parsedSportName ?? "",
      "Article Title": title ?? "",
      Author: parsedAuthorName ?? "",
    });
  };

  const labelClass =
    teams.homeTeam.propBets.length > 1 ? styles.labels : styles.singleLabel;
  return (
    <Card className={styles.cardContainer}>
      <CardHeader className={styles.header}>
        <div className={styles.labelContainer}>
          <div className={labelClass}>
            {teams.homeTeam.propBets.map((bet) => (
              <p key={bet.marketType}>
                <span>{bet.marketName}</span>
              </p>
            ))}
          </div>
        </div>
      </CardHeader>
      <TeamsLine
        eventName={event.name}
        {...teams}
        sessionId={sessionId}
        deviceId={deviceId}
        selections={odds.props.flatMap((prop) =>
          filterEmpty(prop.selections ?? [])
        )}
        isFeaturedBet={isFeaturedBet}
        authorName={authorName}
        sportName={sportName}
      />
      <div className={styles.metaInfo}>
        <span suppressHydrationWarning>
          {odds?.event?.time
            ? getFormattedDateWithTimeAndTimezone(new Date(odds.event.time))
            : null}
        </span>
        <a
          onClick={sbLinkClicked}
          href={ampLink}
          target="_blank"
          rel="noreferrer"
        >
          More odds in Sportsbook
          <ArrowRight height={9} width={5} />
        </a>
      </div>
      <CardFooter className={styles.footer}>
        <p>Odds/lines subject to change</p>
      </CardFooter>
    </Card>
  );
};

export default TeamProps;
